import { useNavigate, useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Anchor, MapPin, Phone, Mail, Globe, Radio, Ship, ArrowLeft, Loader2, Building2, Clock,
} from 'lucide-react';
import { usePortById } from '@/hooks/usePortPros';
import { usePortProfessionnels, SPECIALITES } from '@/hooks/usePortPros';

const PortDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: port, isLoading } = usePortById(id);
  const { data: pros = [], isLoading: prosLoading } = usePortProfessionnels(id);
  const [filter, setFilter] = useState<string | null>(null);

  const specialitesPresentes = useMemo(
    () => Array.from(new Set(pros.map((p) => p.specialite))),
    [pros]
  );

  const prosFiltres = filter ? pros.filter((p) => p.specialite === filter) : pros;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!port) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Port introuvable.</p>
        <Button onClick={() => navigate(-1)} variant="outline">Retour</Button>
      </div>
    );
  }

  const mapsUrl =
    port.latitude && port.longitude
      ? `https://www.google.com/maps?q=${port.latitude},${port.longitude}`
      : null;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="-ml-2">
          <ArrowLeft className="h-4 w-4 mr-1" /> Retour
        </Button>

        {/* En-tête */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-primary/10">
              <Anchor className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{port.nom}</h1>
              <p className="text-muted-foreground">
                {[port.ville, port.code_postal].filter(Boolean).join(' ')}
                {port.departement ? ` — ${port.departement}` : ''}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {port.region && <Badge variant="secondary">{port.region}</Badge>}
            {port.pays && <Badge variant="outline">{port.pays}</Badge>}
          </div>
        </div>

        {/* Infos pratiques */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            {port.description && (
              <p className="text-muted-foreground">{port.description}</p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              {port.telephone && (
                <Info icon={<Phone className="h-4 w-4" />} label="Téléphone">
                  <a href={`tel:${port.telephone}`} className="text-primary hover:underline">{port.telephone}</a>
                </Info>
              )}
              {port.email && (
                <Info icon={<Mail className="h-4 w-4" />} label="Email">
                  <a href={`mailto:${port.email}`} className="text-primary hover:underline break-all">{port.email}</a>
                </Info>
              )}
              {port.site_web && (
                <Info icon={<Globe className="h-4 w-4" />} label="Site web">
                  <a href={port.site_web} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">
                    {port.site_web}
                  </a>
                </Info>
              )}
              {port.vhf_canal && (
                <Info icon={<Radio className="h-4 w-4" />} label="Canal VHF">{port.vhf_canal}</Info>
              )}
              {port.capacite_places != null && (
                <Info icon={<Ship className="h-4 w-4" />} label="Places">{port.capacite_places}</Info>
              )}
              {mapsUrl && (
                <Info icon={<MapPin className="h-4 w-4" />} label="Localisation">
                  <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Voir sur Google Maps
                  </a>
                </Info>
              )}
            </div>
            {port.services && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Services</p>
                <p className="text-sm">{port.services}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Professionnels */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Professionnels du port</h2>
            <Badge variant="secondary">{pros.length}</Badge>
          </div>

          {specialitesPresentes.length > 1 && (
            <div className="flex flex-wrap gap-2">
              <Badge
                variant={filter === null ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilter(null)}
              >
                Tous
              </Badge>
              {specialitesPresentes.map((s) => (
                <Badge
                  key={s}
                  variant={filter === s ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setFilter(s)}
                >
                  {s}
                </Badge>
              ))}
            </div>
          )}

          {prosLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : prosFiltres.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                Aucun professionnel référencé pour ce port pour le moment.
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {prosFiltres.map((pro) => (
                <Card key={pro.id} className="overflow-hidden">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      {pro.logo_url ? (
                        <img
                          src={pro.logo_url}
                          alt={pro.nom_entreprise}
                          className="h-14 w-28 rounded-lg object-cover bg-muted"
                        />
                      ) : (
                        <div className="h-14 w-28 rounded-lg bg-muted flex items-center justify-center">
                          <Building2 className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold leading-tight">{pro.nom_entreprise}</h3>
                        <Badge variant="secondary" className="mt-1">{pro.specialite}</Badge>
                      </div>
                    </div>
                    {pro.description && (
                      <p className="text-sm text-muted-foreground">{pro.description}</p>
                    )}
                    <div className="space-y-1 text-sm">
                      {(pro.adresse || pro.ville) && (
                        <p className="flex items-start gap-2 text-muted-foreground">
                          <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                          <span>{[pro.adresse, pro.code_postal, pro.ville].filter(Boolean).join(', ')}</span>
                        </p>
                      )}
                      {pro.telephone && (
                        <p className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <a href={`tel:${pro.telephone}`} className="text-primary hover:underline">{pro.telephone}</a>
                        </p>
                      )}
                      {pro.email && (
                        <p className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <a href={`mailto:${pro.email}`} className="text-primary hover:underline break-all">{pro.email}</a>
                        </p>
                      )}
                      {pro.site_web && (
                        <p className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-muted-foreground" />
                          <a href={pro.site_web} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">
                            {pro.site_web}
                          </a>
                        </p>
                      )}
                      {pro.horaires && (
                        <p className="flex items-start gap-2 text-muted-foreground">
                          <Clock className="h-4 w-4 mt-0.5 shrink-0" />
                          <span className="whitespace-pre-line">{pro.horaires}</span>
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const Info = ({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) => (
  <div className="flex items-start gap-2">
    <span className="text-muted-foreground mt-0.5">{icon}</span>
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <div>{children}</div>
    </div>
  </div>
);

export default PortDetailPage;
